import _ from 'lodash'
import React from 'react'
import queryString from 'query-string'
import apis from 'browser/app/models/apis'
import { MobileError } from './mobile-error'
import { Logger } from 'browser/apis/logging'
import { browserHistory } from 'browser/history'
import { Entity } from 'shared-libs/models/entity'
import { LRUCacheTTL } from 'browser/app/models/cache'
import { stringifyServerError } from 'shared-libs/models/utils'
import { TruckLoader } from '../components/truck-loader/truck-loader'
import {
  getEntityIds,
  normalizeUrlQuery,
  parseWorkflowTimeout,
  maybeResolveContainerId,
} from 'shared-libs/helpers/deeplink'
import { globalTranslationTable } from '../util/global-translation-table'
import { translateString } from 'shared-libs/helpers/utils'

interface DeepLinkProps {
  location?: Location
}

interface DeepLinkState {
  error?: any
}

export class DeepLink extends React.Component<DeepLinkProps, DeepLinkState> {
  /** Cache w/ TTL that wraps localStorage to map deeplinks to recent entity IDs */
  private entityCache?: LRUCacheTTL<string>

  constructor(props: DeepLinkProps) {
    super(props)

    this.state = {}

    this.entityCache = new LRUCacheTTL()
    this.entityCache.initialize()
  }

  public componentDidMount(): void {
    const url = window.location.href
    const search = queryString.parse(this.props.location.search)
    const props = normalizeUrlQuery(search)
    const entityIds = getEntityIds(props)

    this.handleDocumentAdd(entityIds, url, props)
  }

  public componentWillUnmount(): void {
    this.entityCache.dispose()
  }

  public render(): JSX.Element {
    const { error } = this.state

    const translatedTitle = translateString('Error', globalTranslationTable)
    const translatedDescription = translateString('Mobile_Deeplink_Error_Description', globalTranslationTable)

    if (error) {
      const errorMsg = error.message ? error.message : stringifyServerError(error, '\n')
      return <MobileError title={translatedTitle} description={translatedDescription} errorMessage={errorMsg} />
    }

    return <TruckLoader />
  }

  private handleDocumentAdd = async (entityIds: string[], url: string, props: any = {}) => {
    entityIds = await maybeResolveContainerId(entityIds, apis)

    if (props.targetId && _.size(entityIds) !== 1) {
      this.setState({
        error: new Error('targetId can only be used with exactly one entityId'),
      })
      return
    }

    if (props.targetId) {
      const existingEntity = await this.findExistingEntityByTargetId(props.targetId)
      if (existingEntity) {
        return this.navigateToEntity(existingEntity, props)
      }
    } else {
      const existingEntity = await this.findExistingEntityWithinTimeout(entityIds, url, props)
      if (existingEntity) {
        return this.navigateToEntity(existingEntity, props)
      }
    }

    const entities = await this.createAndSaveEntities(entityIds, url, props)
    if (!entities) {
      return
    }

    this.navigateToEntity(entities[0], props)
  }

  /**
   * Checks the server for a specific entity id using the `targetId` prop. This
   * pins creation deeplinks to a specific entityId for greater reliability, and
   * also enables multi-user deeplinks (send the same deeplink to two users, the
   * first access will create the entity if it doesn't already exist, and
   * subsequent accesses will go straight to entity details).
   */
  private async findExistingEntityByTargetId(targetId: string): Promise<Entity | null> {
    try {
      return await apis.getStore().findRecord(targetId)
    } catch (err) {
      // entity doesn't exist
    }
    return null
  }

  /**
   * Performs a cache lookup to support the `workflowTimeout` concept. This
   * proved to not be so reliable, possibly due to the cache not persisting,
   * depending on how users are accessing the link.
   */
  private async findExistingEntityWithinTimeout(entityIds: string[], url: string, props: any): Promise<Entity | null> {
    // VD-10528 If `workflowTimeout` is set, see if we have an
    // execution entity matching the mixin ID that's been modified or created
    // within the specified time. We check from the cache only, since we specifically
    // want to check locally created records.
    const executionCreateTimeout = parseWorkflowTimeout(props)
    if (!executionCreateTimeout || _.isEmpty(entityIds)) {
      return null
    }

    const entityId = this.entityCache.get(url)
    if (!entityId) {
      return null
    }

    Logger.logEvent('ResumeExistingWorkflow')
    const entity = await apis.getStore().getEntity(entityId)
    if (entity?.isStoryboardExecution) {
      return entity
    }
  }

  private async createAndSaveEntities(entityIds: string[], url: string, props: any): Promise<Entity[] | null> {
    const entities = await apis.getStore().createEntities(entityIds, props)
    if (_.isEmpty(entities)) {
      Logger.logEvent('FailedToCreateEntity', { entityIds })
      this.setState({
        error: new Error('failed to create entity'),
      })
      return null
    }

    if (props.targetId && _.size(entityIds) === 1) {
      // override auto-generated uniqueId with the predetermined targetId
      entities[0].set('uniqueId', props.targetId)
    }

    const entity = entities[0]

    if (_.size(entityIds) === 1 && entity.shouldAutoCreate && entity.isStoryboardExecution) {
      try {
        await entity.save()
        const executionCreateTimeout = parseWorkflowTimeout(props)
        if (executionCreateTimeout && !props.targetId) {
          this.entityCache?.set(url, entity.uniqueId, executionCreateTimeout * 1000)
        }
      } catch (err) {
        console.error('Unable to create workflow', err)
        this.setState({
          error: err,
        })
        return null
      }
    }

    return entities
  }

  private navigateToEntity(entity: Entity, props: any = {}) {
    const search = queryString.parse(this.props.location?.search)
    const { storyId } = props

    const searchParams = {
      ...search,
      jsonProps: undefined,
    }
    if (storyId) {
      searchParams.story_id = storyId
    }

    const entityPath = `/entity/${entity.uniqueId}`
    const entitySearch = queryString.stringify(searchParams)

    if (props.targetId) {
      // Full page navigation so Universal Links can hand the entity off to the native app
      window.location.href = `${entityPath}?${entitySearch}`
      return
    }

    browserHistory.push({
      pathname: entityPath,
      search: entitySearch,
    })
  }
}
